import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  JournalDictationsService,
  JournalEntry,
  JOURNAL_TRANSCRIBING_PLACEHOLDER
} from '../../../../core/services/journal-dictations.service';
import { ClientDictationService } from '../../../../core/services/client-dictation.service';
import { ToastService } from '../../../../core/services/toast.service';

type RecorderKind = 'dictation' | 'voice';
type RecorderState = 'idle' | 'recording' | 'transcribing' | 'error';

@Component({
  selector: 'app-contemplation-notes-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './contemplation-notes-panel.component.html',
  styleUrls: ['./contemplation-notes-panel.component.scss']
})
export class ContemplationNotesPanelComponent implements OnChanges, OnDestroy {
  /** When set, the panel edits an existing journal entry instead of creating one. */
  @Input() editingEntry: JournalEntry | null = null;
  @Input() showBack = false;

  @Output() back = new EventEmitter<void>();
  @Output() entrySaved = new EventEmitter<JournalEntry | null>();

  notesText = '';
  partialDictationText = '';
  dictationSegments: string[] = [];

  recorderState: RecorderState = 'idle';
  recorderKind: RecorderKind = 'dictation';
  errorMessage: string | null = null;
  isDictationLoading = false;
  isDictating = false;
  isFinalizingDictation = false;
  isSavingNote = false;
  savedJournalId: string | null = null;
  noteTitle = 'Journal entry';

  private mediaStream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: BlobPart[] = [];

  private activePollSub?: Subscription;
  private activeEnqueueSub?: Subscription;

  constructor(
    private journalDictations: JournalDictationsService,
    public clientDictation: ClientDictationService,
    private toastService: ToastService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editingEntry']) {
      this.applyEditingEntry();
    }
  }

  ngOnDestroy(): void {
    this.stopAll();
  }

  get isEditing(): boolean {
    return !!this.editingEntry?.id;
  }

  get isEntryTranscribing(): boolean {
    return this.journalDictations.isTranscribing(this.editingEntry);
  }

  get isEditorLocked(): boolean {
    return this.isEntryTranscribing
      || this.isDictationSessionActive
      || this.recorderState === 'transcribing';
  }

  get dictationLiveText(): string {
    const confirmed = this.dictationSegments.join(' ').trim();
    const partial = this.partialDictationText.trim();

    if (confirmed && partial) {
      return `${confirmed} ${partial}`;
    }

    return confirmed || partial;
  }

  get isDictationSessionActive(): boolean {
    return this.isDictationLoading || this.isDictating || this.isFinalizingDictation;
  }

  get canSaveNote(): boolean {
    return !!this.notesText.trim()
      && !this.isSavingNote
      && !this.isEditorLocked
      && this.recorderState !== 'recording';
  }

  get saveButtonLabel(): string {
    if (this.isSavingNote) {
      return 'Saving…';
    }
    return this.isEditing ? 'Update entry' : 'Save to journal';
  }

  onBackClick(): void {
    this.back.emit();
  }

  saveNoteToJournal(): void {
    const content = this.notesText.trim();
    if (!content || this.isSavingNote || this.isEditorLocked) {
      return;
    }

    this.errorMessage = null;
    this.isSavingNote = true;

    const title = this.noteTitle.trim() || 'Journal entry';
    const payload = { content, title, isPrivate: true };

    if (this.isEditing) {
      this.journalDictations.updateEntry(this.editingEntry!.id, payload).subscribe({
        next: entry => this.handleSaveSuccess(entry?.id ?? this.editingEntry!.id, entry),
        error: err => this.handleSaveError(err)
      });
    } else {
      this.journalDictations.createTextDictation(payload).subscribe({
        next: ({ journalId }) => {
          this.handleSaveSuccess(journalId, {
            id: journalId,
            title,
            type: 'TEXT',
            content,
            status: 'PUBLISHED'
          });
        },
        error: err => this.handleSaveError(err)
      });
    }
  }

  startDictation(): void {
    if (this.isEditorLocked || this.recorderState === 'recording') {
      return;
    }

    this.errorMessage = null;
    this.partialDictationText = '';
    this.dictationSegments = [];
    this.isDictationLoading = true;

    this.clientDictation.start({
      onPartial: (text) => {
        this.partialDictationText = text;
      },
      onResult: (text) => {
        this.appendDictationSegment(text);
        this.partialDictationText = '';
      }
    }).then(() => {
      this.isDictationLoading = false;
      this.isDictating = true;
    }).catch(err => {
      console.error(err);
      this.isDictationLoading = false;
      this.isDictating = false;
      this.errorMessage = 'Failed to start local dictation. If this is your first run, prepare the model with npm run prepare:vosk-model.';
      this.recorderState = 'error';
    });
  }

  stopDictation(): void {
    if (!this.isDictating && !this.isDictationLoading) {
      return;
    }

    this.isDictating = false;
    this.isDictationLoading = false;
    this.isFinalizingDictation = true;

    this.clientDictation.stop()
      .then(finalChunk => {
        if (finalChunk) {
          this.appendDictationSegment(finalChunk);
        } else if (this.partialDictationText.trim()) {
          this.appendDictationSegment(this.partialDictationText);
        }

        const captured = this.dictationSegments.join(' ').trim();
        if (captured) {
          this.appendTranscript(captured);
        }

        this.partialDictationText = '';
        this.dictationSegments = [];
      })
      .catch(err => {
        console.error(err);
        this.errorMessage = 'Failed to finalize dictation.';
        this.recorderState = 'error';
      })
      .finally(() => {
        this.isFinalizingDictation = false;
        if (this.recorderState === 'error') {
          this.recorderState = 'idle';
        }
      });
  }

  startVoiceNote(): void {
    this.startRecording('voice');
  }

  stopRecordingAndEnqueue(): void {
    this.stopRecording().then(blob => {
      if (!blob) return;
      this.enqueueBlob(blob);
    });
  }

  private applyEditingEntry(): void {
    this.stopAll();
    this.errorMessage = null;
    this.savedJournalId = null;
    this.recorderState = 'idle';

    if (this.editingEntry) {
      this.noteTitle = (this.editingEntry.title ?? '').trim() || 'Journal entry';
      this.notesText = this.isEntryTranscribing
        ? JOURNAL_TRANSCRIBING_PLACEHOLDER
        : (this.editingEntry.content ?? '');
    } else {
      this.resetForm();
    }
  }

  private resetForm(): void {
    this.notesText = '';
    this.noteTitle = 'Journal entry';
    this.partialDictationText = '';
    this.dictationSegments = [];
    this.savedJournalId = null;
  }

  private handleSaveSuccess(journalId: string | null | undefined, entry: JournalEntry | null): void {
    this.savedJournalId = journalId ?? null;
    this.isSavingNote = false;
    this.toastService.showSuccess(
      this.isEditing ? 'Journal entry updated.' : 'Your note was saved to your journal.'
    );
    this.entrySaved.emit(entry);
  }

  private handleSaveError(err: unknown): void {
    console.error(err);
    this.isSavingNote = false;
    this.errorMessage = 'Failed to save note to your journal.';
    this.recorderState = 'error';
    this.toastService.showError('Could not save your note. Please try again.');
  }

  private startRecording(kind: RecorderKind): void {
    if (this.isEditorLocked || this.recorderState === 'recording') return;

    this.stopAll();

    this.recorderKind = kind;
    this.errorMessage = null;
    this.recorderState = 'recording';

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(stream => {
        this.mediaStream = stream;
        this.audioChunks = [];

        let mimeType = '';
        if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
          mimeType = 'audio/ogg;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/wav')) {
          mimeType = 'audio/wav';
        } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
          mimeType = 'audio/webm';
        }

        this.mediaRecorder = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);

        this.mediaRecorder.ondataavailable = e => {
          if (e.data && e.data.size > 0) {
            this.audioChunks.push(e.data);
          }
        };

        this.mediaRecorder.start(1000);
      })
      .catch(err => {
        console.error(err);
        this.errorMessage = 'Microphone permission denied or not available.';
        this.recorderState = 'error';
        this.stopAll();
      });
  }

  private async stopRecording(): Promise<Blob | null> {
    if (!this.mediaRecorder) return null;

    const recorder = this.mediaRecorder;
    const stream = this.mediaStream;

    return new Promise(resolve => {
      recorder.onstop = () => {
        try {
          const mimeType = recorder.mimeType || 'audio/webm';
          const blob = new Blob(this.audioChunks, { type: mimeType });
          resolve(blob);
        } catch {
          resolve(null);
        } finally {
          this.mediaRecorder = null;
          this.audioChunks = [];
          stream?.getTracks().forEach(t => t.stop());
          this.mediaStream = null;
        }
      };

      if (recorder.state !== 'inactive') {
        recorder.stop();
      } else {
        resolve(null);
      }
    });
  }

  private enqueueBlob(blob: Blob): void {
    this.recorderState = 'transcribing';
    this.errorMessage = null;
    this.stopPoll();

    const title = this.noteTitle.trim() || 'Journal voice note';

    this.activeEnqueueSub = this.journalDictations.enqueueDictation(blob, {
      title,
      is_private: true
    }).subscribe({
      next: ({ journalId }) => {
        this.toastService.showSuccess('Voice note uploaded. Transcription in progress…');
        this.recorderState = 'idle';
        this.entrySaved.emit({
          id: journalId,
          title,
          type: 'AUDIO',
          content: JOURNAL_TRANSCRIBING_PLACEHOLDER,
          status: 'DRAFT'
        });
      },
      error: err => {
        console.error(err);
        this.errorMessage = 'Failed to upload audio for transcription.';
        this.recorderState = 'error';
        this.stopAll();
      }
    });
  }

  private stopPoll(): void {
    this.activePollSub?.unsubscribe();
    this.activePollSub = undefined;
  }

  private appendDictationSegment(text: string): void {
    const cleaned = (text ?? '').trim();
    if (!cleaned) return;

    const lastSegment = this.dictationSegments[this.dictationSegments.length - 1];
    if (lastSegment === cleaned) return;

    this.dictationSegments.push(cleaned);
  }

  private appendTranscript(text: string): void {
    const cleaned = (text ?? '').trim();
    if (!cleaned) return;

    if (!this.notesText.trim() || this.notesText === JOURNAL_TRANSCRIBING_PLACEHOLDER) {
      this.notesText = cleaned;
      return;
    }

    const needsSpace = !/[\s\n]$/.test(this.notesText);
    this.notesText = `${this.notesText}${needsSpace ? ' ' : ''}${cleaned}`;
  }

  private stopAll(): void {
    this.stopPoll();
    this.activeEnqueueSub?.unsubscribe();
    this.activeEnqueueSub = undefined;

    if (this.isDictating || this.isDictationLoading) {
      void this.clientDictation.stop();
    }

    this.isDictating = false;
    this.isDictationLoading = false;
    this.isFinalizingDictation = false;
    this.partialDictationText = '';
    this.dictationSegments = [];

    try {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
    } catch {
      // ignore
    }
    this.mediaRecorder = null;

    this.audioChunks = [];
    this.mediaStream?.getTracks().forEach(t => t.stop());
    this.mediaStream = null;
  }
}
